import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Sparkle, Lightning, ArrowLeft } from "@phosphor-icons/react";

const FEATURES_SUBSCRIPTION = [
  "Everything you already have in the one-off plan",
  "Actuals tracking with side-by-side variance",
  'AI-powered monthly insights ("what is this telling you?")',
  "Rolling 12-month horizon (auto-extends as months close)",
  "Goals with AI-suggested next actions",
  "Historic months + insights saved forever",
  "Friendly monthly reminder emails",
];

export default function Upgrade() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");

  const isCancelled = user?.plan === "cancelled";
  const isSubscribed = user?.plan === "subscription";

  // In-app upgrade flow. Existing users choose monthly or annual and get bounced to
  // Stripe Checkout. Promo codes for money-off are handled by Stripe's own field on
  // the checkout page (allow_promotion_codes:true). Custom-effect offers (free
  // months / extended trial) run via URL campaigns from the marketing site — they
  // don't apply in-app.
  const startCheckout = async (uiKey) => {
    const kind = uiKey === "annual" ? "annual" : "monthly";
    setBusy(uiKey);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        kind,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/account"
          className="inline-flex items-center gap-2 text-sm text-black/70 hover:text-black mb-6"
          data-testid="upgrade-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back to account
        </Link>

        <div className="section-eyebrow flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> {isSubscribed ? "You're in" : "Upgrade"}
        </div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-2 leading-[1.05]">
          {isSubscribed ? "You're " : "Ready when "}
          <span className="highlight-yellow inline-block">
            {isSubscribed ? "already in" : "you are"}
          </span>
          .
        </h1>

        {isSubscribed ? (
          <p className="mt-3 text-black/70 max-w-2xl">
            You're on the {user?.billing_cycle === "annual" ? "annual" : "monthly"} subscription.
            Manage your billing or cancel anytime from your account.
          </p>
        ) : (
          <p className="mt-3 text-black/70 max-w-2xl">
            {isCancelled
              ? "Welcome back. Pick monthly or annual to reactivate — your forecast, actuals, insights and goals are all still here."
              : "Pick monthly or annual — same forecast, same features, just pick your rhythm."}
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-6 items-start mt-8">
          {/* Monthly £19/mo */}
          <div className="card flex flex-col" data-testid="plan-card-subscription">
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
                <Sparkle size={22} weight="fill" /> Monthly
              </h2>
            </div>
            <div className="mt-4 font-heading text-5xl">
              £19<span className="text-2xl">/mo</span>
            </div>
            <p className="text-black/65 mt-1">cancel anytime · no setup fee (you've paid it)</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {FEATURES_SUBSCRIPTION.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Lightning size={18} weight="fill" className="text-black mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              className="btn-secondary mt-6"
              onClick={() => startCheckout("subscription")}
              disabled={!!busy || isSubscribed}
              data-testid="checkout-subscription-btn"
            >
              {busy === "subscription"
                ? "Loading…"
                : isSubscribed
                ? "You're on this plan"
                : isCancelled
                ? "Reactivate monthly"
                : "Start monthly"}
            </button>
          </div>

          {/* Annual £190/yr */}
          <div className="card-yellow flex flex-col relative" data-testid="plan-card-annual">
            <div className="absolute -top-3 right-4 chip bg-black text-white border-black uppercase tracking-wide">
              Save £38
            </div>
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
                <Sparkle size={22} weight="fill" /> Annual
              </h2>
              <span className="chip bg-white text-black border-black">Best value</span>
            </div>
            <div className="mt-4 font-heading text-5xl">
              £190<span className="text-2xl">/yr</span>
            </div>
            <p className="text-black/75 mt-1">
              <span className="line-through text-black/50">£228/yr</span> · just £15.83/mo equivalent
            </p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {FEATURES_SUBSCRIPTION.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Lightning size={18} weight="fill" className="text-black mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
              <li className="flex items-start gap-2 text-sm font-semibold">
                <Lightning size={18} weight="fill" className="text-black mt-0.5 flex-shrink-0" />
                <span>Two months free vs paying monthly</span>
              </li>
            </ul>
            <button
              className="btn-primary mt-6"
              onClick={() => startCheckout("annual")}
              disabled={!!busy || isSubscribed}
              data-testid="checkout-annual-btn"
            >
              {busy === "annual"
                ? "Loading…"
                : isSubscribed
                ? "You're on a plan already"
                : "Go annual & save"}
            </button>
          </div>
        </div>

        <div className="ai-insight mt-10" data-testid="promo-hint">
          <strong>Got a promo code?</strong> Enter it in the "Add promotion code" field on
          Stripe's checkout page after you pick a plan — money-off codes apply automatically.
        </div>

        <div className="mt-6 text-sm text-black/65 flex flex-wrap gap-x-6 gap-y-2">
          <span className="inline-flex items-center gap-1">
            <CheckCircle size={14} weight="fill" /> Secure Stripe checkout
          </span>
          <span className="inline-flex items-center gap-1">
            <CheckCircle size={14} weight="fill" /> Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1">
            <CheckCircle size={14} weight="fill" /> No hidden fees
          </span>
        </div>
      </div>
    </div>
  );
}
